const {
    formatHours,
    formatMinutes
} = require('./rateCardUtils')
const {
    lightFormat: format,
    set
} = require('date-fns')

const BOOKING_STATUS_OPTIONS = [
    {value: 'PENDING_ENTRY', label: 'Pending entry'}, // entry is NULL
    {value: 'CURRENT', label: 'Current'}, // entry not NULL, exit is NULL
    {value: 'CLOSED', label: 'Closed'}, // (exit not NULL) or (timeout passed)
    {value: 'FINALISED', label: 'Finalised'}, // payment, if any, was processed
    {value: 'CANCELED', label: 'Canceled'},
    { value: 'ERROR', label: 'Error' },
]

const BOOKING_STATUS_OPTIONS_WITHOUT_FINALISED = [
    {value: 'PENDING_ENTRY', label: 'Pending entry', disabled: true}, // entry is NULL
    {value: 'CURRENT', label: 'Current'}, // entry not NULL, exit is NULL
    {value: 'CLOSED', label: 'Closed'}, // (exit not NULL) or (timeout passed)
    {value: 'CANCELED', label: 'Canceled'},
]

const RYDGES_GEELONG_INSTALLATION_ID = 'da2ae218-953c-41ef-97e9-f6f1fa598d9d'

const BOOKING_FOC_REASONS = [
    {value: 'GUEST_COMPLAINT_HOTEL', label: 'Guest Complaint - Hotel'},
    {value: 'GUEST_COMPLAINT_PARKING', label: 'Guest Complaint - Parking'},
    {value: 'PARKING_FULL', label: 'Parking Full'},
    {value: 'VIP_GUEST', label: 'VIP Guest'},
    {value: 'HOTEL_OWNERS', label: 'Hotel Owners'},
    {value: 'QR_PLATE_ADD', label: 'QR Entry / Exit Plate Add'},
    {value: 'BROWNIE_POINTS', label: 'Brownie points'},
]

const BOOKING_FOC_REASONS_RYDGES_GEELONG = [
    {value: 'GUEST_COMPLAINT_HOTEL', label: 'Guest Complaint / Service Recovery'},
    {value: 'VIP_GUEST', label: 'VIP Guest'},
    {value: 'HOTEL_OWNERS', label: 'Hotel Owners'},
    {value: 'CONFERENCE_ORGANISER', label: 'Conference Organiser'},
    {value: 'SITE_INSPECTION', label: 'Site Inspection'},
    {value: 'SALES_COURTESY', label: 'Sales Courtesy'},
]

function getBookingFocReasons(installationId) {
    if (installationId === RYDGES_GEELONG_INSTALLATION_ID) {
        return BOOKING_FOC_REASONS_RYDGES_GEELONG
    }
    return BOOKING_FOC_REASONS
}

/*
 * Lookup label for a stored FOC reason. Falls back across both lists so that
 * historical bookings still render correctly even after a facility's options change.
 */
function getBookingFocReasonLabel(reason, installationId) {
    if (!reason) return ''
    const list = getBookingFocReasons(installationId)
    const match = list.find(r => r.value === reason)
    if (match) return match.label
    const fallback = BOOKING_FOC_REASONS.find(r => r.value === reason)
        || BOOKING_FOC_REASONS_RYDGES_GEELONG.find(r => r.value === reason)
    return fallback ? fallback.label : reason
}

const SUPPORT_TYPE_OPTIONS = [
    {value: 'GATE_NOT_OPEN', label: 'Gate Not Opening'},
    {value: 'WEBSITE_DOWN', label: 'Website Down'},
    {value: 'CAMERA_NOT_WORKING', label: 'Camera Not Working'},
    {value: 'OTHER', label: 'Other'}
]

function getTicketText(supportTypeValue) {
    return SUPPORT_TYPE_OPTIONS.find(option => option.value === supportTypeValue)?.label;
}

const BOOKING_STATUS_SEARCH_OPTIONS = [
    {value: '', label: 'All bookings'}
].concat(BOOKING_STATUS_OPTIONS)

const INSTALLATION_TRANSACTIONS_SEARCH_OPTIONS = [
    {value: 'current', label: 'Current Installation'},
    {value: 'all', label: 'All Installations'},
]

const EVENT_GATE_TYPES = [
    {value: 'ENTRY', label: 'Entry'},
    {value: 'EXIT', label: 'Exit'}
]

const INSTALLATION_STATUS_OPTIONS = [
    {value: 'PENDING', label: 'Pending activation'},
    {value: 'ACTIVE', label: 'Active'},
    {value: 'DISABLED', label: 'Disabled'}
]

const RATECARD_TYPE_OPTIONS = [
    {value: 'DAILY', label: 'Daily'},
    {value: 'HOURLY', label: 'Hourly'},
    {value: 'TIERED_HOURS', label: 'Tiered Hours'},
    {value: 'MONTHLY', label: 'Monthly'},
    {value: 'FIXED_SESSION', label: 'Fixed Session'},
]

// ISO 8601 day-of-week numbering: 1=Mon ... 7=Sun. Used by FIXED_SESSION cards.
const ISO_DAYS_OF_WEEK = [
    {value: 1, label: 'Mon'},
    {value: 2, label: 'Tue'},
    {value: 3, label: 'Wed'},
    {value: 4, label: 'Thu'},
    {value: 5, label: 'Fri'},
    {value: 6, label: 'Sat'},
    {value: 7, label: 'Sun'},
]

const REFUND_STATUS_OPTIONS = [
    {value: 'PENDING', label: 'Pending processing'},
    {value: 'PROCESSED', label: 'Processed successfully'},
    {value: 'REJECTED', label: 'Payment not made'},
]

const ENTRY_EXIT_24HR_OPTIONS = Array.from({length: 24 * 4}, (_, i) => {
    const minutesPast = i * 15 // every 15 minutes

    return {
        label: format(set(new Date(), {
            hours: 0,
            minutes: minutesPast
        }), 'HH:mm'),
        value: minutesPast
    }
})

const GRACE_PERIOD_OPTIONS = [
    0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, // 5 minute increments up to 1 hour
    60, 70, 80, 90, 100, 110, // 10 minute increments up to 2 hours
    120, 150, 180, 210, 240, 270, 300, 330, // 30 min increments up to 6 hours
    360, 420, 480, 540, 600, 660, 720, 780, 840, 900, 960, 1020, 1080, 1140, 1200, 1260, 1320, 1380, 1440, // 1 hour increments up to 24 hours
].map((value) => ({
    label: formatMinutes(value),
    value
}))

const EXPIRY_HOURS_OPTIONS = [].concat(
    [2, 4, 8, 12],
    Array.from({length: 14}, (_, i) => (1 + i) * 24)
).map((value) => ({
    label: formatHours(value),
    value
}))

/*
 * SUPPORT TYPE SEARCH FILTER OPTIONS USED IN THE SUPPORT TICKET LIST PAGE
 * Used in: src/pages/Support/SearchFilterPanel.js
 */
const SUPPORT_TYPE_SEARCH_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'Gate Not Opening', label: 'Gate Not Opening' },
    { value: 'Refund', label: 'Refund' },
    { value: 'Gate Refund', label: 'Gate Refund' },
    { value: 'Power Outage', label: 'Power Outage' },
    { value: 'Internet Outage', label: 'Internet Outage' },
    { value: 'Not Recognizing Plate', label: 'Not Recognizing Plate' },
    { value: 'Payment at Gate Not Processing', label: 'Payment at Gate Not Processing' },
    { value: 'Not Opening for Vehicles but Opening for Intercom', label: 'Not Opening for Vehicles but Opening for Intercom' },
    { value: 'Gate Damaged', label: 'Gate Damaged' },
    { value: 'Page Not Loading', label: 'Page Not Loading' },
    { value: 'Console Streams Not Showing', label: 'Console Streams Not Showing' },
    { value: 'Console Not Allowing Booking', label: 'Console Not Allowing Booking' },
    { value: 'QR Code Not Working', label: 'QR Code Not Working' },
    { value: 'Camera Damaged', label: 'Camera Damaged' },
    { value: 'Kiosk Damaged', label: 'Kiosk Damaged' },
]

/*
 * SUPPORT TICKET STATUS SEARCH FILTER OPTIONS (OPEN/RESOLVED)
 * Used in: src/pages/Support/SearchFilterPanel.js
 */
const SUPPORT_STATUS_SEARCH_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'OPEN', label: 'Open' },
    { value: 'RESOLVED', label: 'Resolved' },
]

const RATECARD_CALCULATION_MODE_OPTIONS = [
    { value: 'LEGACY', label: 'Legacy' },
    { value: 'MULTI_DATE_OVERNIGHT', label: 'Multi-date Overnight' },
    { value: 'MULTI_DATE_DAYTIME', label: 'Multi-date Daytime' },
    { value: 'MULTI_DATE_FIXED_PACKAGE', label: 'Fixed Package (multi-date)' },
    { value: 'SINGLE_DAY', label: 'Single-day' },
]

function getRateCardCalculationModes (isDaily, isMultiDay, currentMode) {
    const defaultOptions = [];
    // use LEGACY mode only for legacy cards, disable this option for new cards
    if(currentMode && currentMode === 'LEGACY') defaultOptions.push(RATECARD_CALCULATION_MODE_OPTIONS.find(el => el.value === 'LEGACY'));
    if (!isDaily) return defaultOptions;

    if (isMultiDay) return [
        ...defaultOptions,
        ...RATECARD_CALCULATION_MODE_OPTIONS.filter(el => el.value.indexOf('MULTI') !== -1)
    ];

    return [
        ...defaultOptions,
        RATECARD_CALCULATION_MODE_OPTIONS.find(el => el.value === 'SINGLE_DAY'),
    ]
}

export {
    BOOKING_STATUS_OPTIONS,
    BOOKING_STATUS_SEARCH_OPTIONS,
    EVENT_GATE_TYPES,
    EXPIRY_HOURS_OPTIONS,
    INSTALLATION_STATUS_OPTIONS,
    BOOKING_FOC_REASONS,
    BOOKING_FOC_REASONS_RYDGES_GEELONG,
    RYDGES_GEELONG_INSTALLATION_ID,
    getBookingFocReasons,
    getBookingFocReasonLabel,
    RATECARD_TYPE_OPTIONS,
    REFUND_STATUS_OPTIONS,
    SUPPORT_TYPE_OPTIONS,
    SUPPORT_TYPE_SEARCH_OPTIONS,
    SUPPORT_STATUS_SEARCH_OPTIONS,
    BOOKING_STATUS_OPTIONS_WITHOUT_FINALISED,
    INSTALLATION_TRANSACTIONS_SEARCH_OPTIONS,

    ENTRY_EXIT_24HR_OPTIONS,
    GRACE_PERIOD_OPTIONS,
    ISO_DAYS_OF_WEEK,

    getTicketText,
    getRateCardCalculationModes
}

